import React, { useEffect, useState } from "react";
import { Box, Avatar, Chip, Typography, IconButton, Menu, MenuItem, Paper } from "@mui/material";
import Tablecomponent from "../../components/reusableComponents/Tablecomponent";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import InstagramIcon from "@mui/icons-material/Instagram";
import { useHistory } from "react-router-dom";
import ApiService from "../../_services/ApiService";
import SearchInput from "../../components/reusableComponents/reusableSearchInput";
import ReusableComponent from "../../components/reusableComponents/reusableInputComponents";

const GigApplicationPage = ({ applications, gigData, gigId, refreshGigDetails }) => {
    const history = useHistory();
    const [selectedRows, setSelectedRows] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedApplication, setSelectedApplication] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const isFinalStatus = (status) =>
        status === "approved" || status === "rejected";


    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case "approved":
                return { bg: "#EBFFF7", color: "#219653" };
            case "rejected":
                return { bg: "#FFF2F2", color: "#EB5757" };
            case "pending":
            case "inprogress":
                return { bg: "#FFF9E6", color: "#F2994A" };
            case "need-changes":
                return { bg: "#F5F6FF", color: "#6B7280" };
            default:
                return { bg: "#F5F6FF", color: "#6B7280" };
        }
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(search);
        }, 500); 

        return () => {
            clearTimeout(handler);
        };
    }, [search]);

    useEffect(() => {
        refreshGigDetails({
            limit: rowsPerPage,
            offset: page * rowsPerPage,
            Status: statusFilter,
            search: debouncedSearch,
        });
    }, [debouncedSearch, statusFilter]);
    const handleMenuOpen = (event, application) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setSelectedApplication(application);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const updateApplicationStatus = async (status, ids) => {
        try {
            const payload = {
                Status: status,
                ids,
            };

            const response = await ApiService.put(
                "gigs_apply_Update",
                payload
            );

            if (response?.status === 200) {
                refreshGigDetails();
            } else {
                console.error("API Error:", response);
            }
        } catch (error) {
            console.error("Status update failed:", error);
        }
    };

    const handleApproveSelected = async (ids) => {
        if (!ids.length) return;
        await updateApplicationStatus("approved", ids);
    };

    const handleRejectSelected = async (ids) => {
        if (!ids.length) return;
        await updateApplicationStatus("rejected", ids);
    };

    const handleRowClick = (row) => {
        history.push({
            pathname: `/gig-management/view-gig/${gigId}/application/${row.id}/pitch`,
            state: { applicationData: row, gigData }
        });
    };
    const formatFollowers = (num) => {
        if (num === null || num === undefined) return "";
        if (num >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "m";
        if (num >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, "") + "k";
        return num.toString();
    };
    const columns = [
        {
            field: "checkbox",
            headerName: "",
            sortable: false,
        },
        {
            field: "profile",
            headerName: "Profile",
            sortable: false,
            renderCell: ({ row }) => (
                <Box display="flex" alignItems="center" gap={2}>
                    <Avatar
                        src={row.creator_logo}
                        alt={row.Name}
                        sx={{ width: 40, height: 40 }}
                    >
                        {row.Name?.charAt(0)?.toUpperCase()}
                    </Avatar>
                    <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: 14, color: "#222" }}>
                            {row.Name || "N/A"}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: "#7A7A7A" }}>
                            {row.creator_agency_id || "N/A"}
                        </Typography>
                        {row.handle_name && (
                            <Typography sx={{ fontSize: 12, color: "#7A7A7A" }}>
                                {row.handle_name}
                            </Typography>
                        )}
                    </Box>
                </Box>
            ),
        },
        {
            field: "applied",
            headerName: "Applied",
            sortable: true,
            renderCell: ({ row }) => {
                const date = new Date(row.created_at);
                return (
                    <Typography sx={{ fontSize: 14, color: "#4A525F" }}>
                        {date.toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                        })}
                    </Typography>
                );
            },
        },
        {
            field: "followers",
            headerName: "Followers/Subscribers",
            sortable: true,
            renderCell: ({ row }) => (
                <Box display="flex" alignItems="center" gap={1}>
                    {row.content_format === "instagram" && (
                        <InstagramIcon sx={{ fontSize: 18, color: "#E4405F" }} />
                    )}
                    <Typography sx={{ fontSize: 14, color: "#4A525F" }}>
                        {row.followers ? formatFollowers(row.followers) : "N/A"}
                    </Typography>
                </Box>
            ),
        },
        {
            field: "location",
            headerName: "Location",
            sortable: true,
            renderCell: ({ row }) => (
                <Box>
                    <Typography sx={{ fontSize: 14, color: "#4A525F" }}>
                        {row.address || "N/A"}
                    </Typography>
                    {row.landmark && (
                        <Typography sx={{ fontSize: 12, color: "#7A7A7A" }}>
                            {row.landmark}
                        </Typography>
                    )}
                </Box>
            ),
        },
        {
            field: "status",
            headerName: "Application Status",
            sortable: true,
            renderCell: ({ row }) => {
                const statusConfig = getStatusColor(row.Status);
                return (
                    <Chip
                        label={row.Status || "Pending"}
                        sx={{
                            backgroundColor: statusConfig.bg,
                            color: statusConfig.color,
                            fontWeight: 500,
                            fontSize: 12,
                            textTransform: "capitalize",
                            border: "none",
                        }}
                    />
                );
            },
        },
        {
            field: "deliverables_status",
            headerName: "Deliverables Status",
            sortable: true,
            renderCell: ({ row }) => {
                if (!row.deliverables_status) {
                    return <Typography sx={{ fontSize: 14, color: "#7A7A7A" }}>NA</Typography>;
                }
                const statusConfig = getStatusColor(row.deliverables_status);
                return (
                    <Chip
                        label={row.deliverables_status}
                        sx={{
                            backgroundColor: statusConfig.bg,
                            color: statusConfig.color,
                            fontWeight: 500,
                            fontSize: 12,
                            textTransform: "capitalize",
                            border: "none",
                        }}
                    />
                );
            },
        },
        {
            field: "live_status",
            headerName: "Live Post Status",
            sortable: true,
            renderCell: ({ row }) => {
                if (!row.live_status) {
                    return <Typography sx={{ fontSize: 14, color: "#7A7A7A" }}>NA</Typography>;
                }
                const statusConfig = getStatusColor(row.live_status);
                return (
                    <Chip
                        label={row.live_status}
                        sx={{
                            backgroundColor: statusConfig.bg,
                            color: statusConfig.color,
                            fontWeight: 500,
                            fontSize: 12,
                            textTransform: "capitalize",
                            border: "none",
                        }}
                    />
                );
            },
        },
        {
            field: "actions",
            headerName: "",
            sortable: false,
            renderCell: ({ row }) => (
                <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, row)}
                >
                    <MoreVertIcon />
                </IconButton>
            ),
        },
    ];

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
        refreshGigDetails({
            limit: rowsPerPage,
            offset: newPage * rowsPerPage,
            Status: statusFilter,
            search: debouncedSearch,
        });
    };

    const handleRowsPerPageChange = (event) => {
        const newLimit = parseInt(event.target.value, 10);
        setRowsPerPage(newLimit);
        setPage(0);

        refreshGigDetails({
            limit: newLimit,
            offset: 0,
            Status: statusFilter,
            search: debouncedSearch,
        });
    };


    return (
        <>
            <Paper
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 2,
                    mb: 2,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <SearchInput
                        placeholder="Search"
                        value={search}
                        onChange={(e) => {
                            setPage(0);        
                            setSearch(e.target.value);
                        }}
                        sx={{ width: "350px" }}
                    />

                    {/* Status filter */}
                    <ReusableComponent
                        type="select"
                        label="Application Status"
                        placeholder="All"
                        value={statusFilter}
                        onChange={(option) => {
                            setPage(0);  
                            setStatusFilter(option?.value || "");
                        }}
                        options={[
                            { label: "Approved", value: "approved" },
                            { label: "Rejected", value: "rejected" },
                            { label: "Inprogress", value: "inprogress" },
                        ]}
                        sx={{ width: 220 }}
                        size="small"
                    />
                </Box>
            </Paper>


            <Tablecomponent
                loader={false}
                columns={columns}
                data={applications.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)}
                rowsPerPage={rowsPerPage}
                page={page}
                count={applications.length}
                onPageChange={handlePageChange}
                rowsPerPageOptions={[5, 10, 25, 50]}
                onRowsPerPageChange={handleRowsPerPageChange}
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
                onApproveSelected={handleApproveSelected}
                onRejectSelected={handleRejectSelected}
                onRowClick={handleRowClick}
                message="No applications yet"
                content="Applications will appear here once creators apply to this gig"
                bulkActions={['approve', 'reject']}
                groupBy="Name"
                groupExpandable={true}
                defaultExpanded={false}
            />
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={() => {
                    handleRowClick(selectedApplication);
                    handleMenuClose();
                }}>
                    View Details
                </MenuItem>
                {!isFinalStatus(selectedApplication?.Status) && (<MenuItem onClick={() => {
                    updateApplicationStatus("approved", [selectedApplication.id]);
                    handleMenuClose();
                }}>
                    Approve
                </MenuItem>)}
                {!isFinalStatus(selectedApplication?.Status) && (<MenuItem onClick={() => {
                    updateApplicationStatus("rejected", [selectedApplication.id]);
                    handleMenuClose();
                }}>
                    Reject
                </MenuItem>)}
            </Menu>
        </>
    );
};

export default GigApplicationPage;
